import React, { useState, useRef, useEffect } from "react";
import {
    Avatar,
    Box,
    Button,
    IconButton,
    useMediaQuery,
    useTheme,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Divider,
    Collapse,
    InputBase,
    Badge,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import SettingsIcon from "@mui/icons-material/Settings"; // Added gear icon
import {
    AddShoppingCart,
    FavoriteBorder,
    ExpandLess,
    ExpandMore,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../State/Store";
import { searchProduct } from "../../../State/customer/productSlice";
import { fetchAllCategories } from "../../../State/customer/categorySlice";
import CategorySheet from "./CategorySheet";

export interface Category {
    id?: number;
    name: string;
    categoryId: string;
    parentCategoryId?: string | null;
    level: number;
    parentCategoryName?: string;
    active?: boolean;
    children?: Category[];
}

// Updated color matcher: Industrial Charcoal and Vibrant Safety Orange
const NAVY = "#1E293B"; 
const AMBER = "#F97316"; 

const KithimuLogo = () => (
    <div className="flex items-center gap-2 group">
        <div className="relative flex items-center justify-center p-1.5 bg-orange-500 rounded-lg text-white shadow-sm transition-transform duration-200 group-hover:scale-105">
            <SettingsIcon sx={{ fontSize: 22 }} className="animate-spin-slow" />
        </div>
        <img
            src="/logo.png"
            alt="Power Tools logo"
            className="h-9 sm:h-11 w-auto object-contain transition-transform duration-200 group-hover:scale-105"
            onError={(e) => {
                e.currentTarget.style.display = "none";
            }}
        />
        <span className="whitespace-nowrap font-extrabold text-lg sm:text-xl tracking-tight uppercase text-slate-900">
            Power
            <span className="ml-1 font-semibold text-orange-600">Tools</span>
        </span>
    </div>
);

interface SearchFormProps {
    searchQuery: string;
    setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
    handleSearchSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
    searchInputRef: React.RefObject<HTMLInputElement | null>;
}

const SearchForm: React.FC<SearchFormProps> = ({
    searchQuery,
    setSearchQuery,
    handleSearchSubmit,
    searchInputRef,
}) => (
    <form
        onSubmit={handleSearchSubmit}
        role="search"
        className="flex w-full max-w-[650px] items-center rounded-full border border-slate-200 bg-white px-3 sm:px-4 py-1.5 shadow-sm transition-all duration-200 hover:border-slate-300 focus-within:border-orange-500 focus-within:ring-2 focus-within:ring-orange-100"
    >
        <SearchIcon
            sx={{ fontSize: 21, color: "#64748B" }}
            className="mr-2 shrink-0"
        />

        <InputBase
            inputRef={searchInputRef}
            placeholder="Search products, brands and categories..."
            inputProps={{ "aria-label": "Search products" }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-slate-800"
            sx={{
                fontSize: { xs: "12px", sm: "14px" },
                "& input": {
                    padding: "5px 0",
                },
            }}
        />

        {searchQuery && (
            <IconButton
                type="button"
                aria-label="Clear search"
                onClick={() => setSearchQuery("")}
                size="small"
                sx={{ color: "#64748B" }}
            >
                <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
        )}

        <button
            type="submit"
            className="ml-2 hidden shrink-0 rounded-full bg-orange-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-orange-600 sm:block"
        >
            Search
        </button>
    </form>
);

const Navbar: React.FC = () => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

    const [selectedCategory, setSelectedCategory] = useState("");
    const [showCategorySheet, setShowCategorySheet] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
    const [expandedSubCategory, setExpandedSubCategory] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState("");

    const searchInputRef = useRef<HTMLInputElement>(null);

    const { auth, cart, wishlist, category: categoryState } =
        useAppSelector((state) => state);

    const rawCategories: Category[] = categoryState?.categories || [];

    const categories = rawCategories.filter(
        (category) => category.active !== false
    );

    const mainCategories = categories.filter(
        (category) => category.level === 1
    );

    useEffect(() => {
        dispatch(fetchAllCategories());
    }, [dispatch]);

    useEffect(() => {
        if (
            mainCategories.length > 0 &&
            !mainCategories.some((category) => category.categoryId === selectedCategory)
        ) {
            setSelectedCategory(mainCategories[0].categoryId);
        }
    }, [mainCategories, selectedCategory]);

    useEffect(() => {
        if (!isLarge) {
            setShowCategorySheet(false);
        }
    }, [isLarge]);

    const handleDrawerToggle = () => {
        setMobileMenuOpen((open) => !open);
    };

    const handleCategoryToggle = (categoryId: string) => {
        setExpandedCategory((current) =>
            current === categoryId ? null : categoryId
        );
        setExpandedSubCategory(null);
    };

    const handleSubCategoryToggle = (categoryId: string) => {
        setExpandedSubCategory((current) =>
            current === categoryId ? null : categoryId
        );
    };

    const handleDirectNavigation = (path: string) => {
        setMobileMenuOpen(false);
        setShowCategorySheet(false);
        navigate(path);
    };

    const handleSearchSubmit = (
        event: React.FormEvent<HTMLFormElement>
    ) => {
        event.preventDefault();

        const trimmedQuery = searchQuery.trim();

        if (!trimmedQuery) return;

        dispatch(searchProduct(trimmedQuery));
        navigate(`/products?search=${encodeURIComponent(trimmedQuery)}`);
        setSearchQuery("");
        setMobileMenuOpen(false);
        setShowCategorySheet(false);
    };

    const getSubCategories = (mainCat: Category): Category[] => {
        const subCategories =
            mainCat.children && mainCat.children.length > 0
                ? mainCat.children
                : categories.filter(
                      (category) =>
                          category.level === 2 &&
                          category.parentCategoryId === mainCat.categoryId
                  );

        return subCategories.filter((category) => category.active !== false);
    };

    const getItemCategories = (subCat: Category): Category[] => {
        const itemCategories =
            subCat.children && subCat.children.length > 0
                ? subCat.children
                : categories.filter(
                      (category) =>
                          category.level === 3 &&
                          category.parentCategoryId === subCat.categoryId
                  );

        return itemCategories.filter((category) => category.active !== false);
    };

    return (
        <>
            <Box
                className="sticky top-0 left-0 right-0 border-b border-slate-200 bg-white"
                sx={{
                    zIndex: 1100,
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 2px 12px rgba(15, 23, 42, 0.05)",
                }}
            >
                {/* Customer support bar */}
                <div className="flex items-center justify-between border-b border-slate-800 bg-slate-900 px-3 py-2 text-xs font-medium text-slate-200 sm:px-6 lg:px-20">
                    <div className="flex items-center gap-1.5">
                        <PhoneIcon sx={{ fontSize: 15, color: AMBER }} />
                        <span className="hidden text-slate-400 sm:inline">
                            Customer Support:
                        </span>
                        <a
                            href="tel:0720380744"
                            className="font-bold text-orange-400 transition-colors hover:text-orange-300"
                        >
                            0720 380 744
                        </a>
                    </div>

                    <div className="hidden items-center gap-4 text-[11px] text-slate-300 sm:flex">
                        <button
                            type="button"
                            onClick={() => handleDirectNavigation("/account/orders")}
                            className="transition-colors hover:text-orange-400"
                        >
                            Track Orders
                        </button>
                        <span className="text-slate-600">|</span>
                        <button
                            type="button"
                            onClick={() => handleDirectNavigation("/contact")}
                            className="transition-colors hover:text-orange-400"
                        >
                            Help &amp; Support
                        </button>
                    </div>
                </div>

                {/* Main header */}
                <div className="flex h-[62px] items-center justify-between gap-2 px-3 sm:h-[72px] sm:gap-4 sm:px-6 lg:px-20">
                    <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                        {!isLarge && (
                            <IconButton
                                onClick={handleDrawerToggle}
                                aria-label="Open navigation menu"
                                size="small"
                                sx={{
                                    color: NAVY,
                                    borderRadius: 2,
                                    "&:hover": { backgroundColor: "#F1F5F9" },
                                }}
                            >
                                <MenuIcon sx={{ fontSize: 26 }} />
                            </IconButton>
                        )}

                        <button
                            type="button"
                            onClick={() => handleDirectNavigation("/")}
                            aria-label="Power Tools home"
                            className="flex items-center"
                        >
                            <KithimuLogo />
                        </button>
                    </div>

                    {/* Desktop search */}
                    {isLarge && (
                        <div className="flex min-w-0 flex-1 justify-center px-4">
                            <SearchForm
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                handleSearchSubmit={handleSearchSubmit}
                                searchInputRef={searchInputRef}
                            />
                        </div>
                    )}

                    {/* Account, wishlist and cart */}
                    <div className="flex shrink-0 items-center gap-0.5 sm:gap-2">
                        {auth.user ? (
                            <IconButton
                                onClick={() => handleDirectNavigation("/account/orders")}
                                aria-label="My account and orders"
                                sx={{
                                    gap: 1,
                                    borderRadius: 2,
                                    color: NAVY,
                                    "&:hover": { backgroundColor: "#F8FAFC" },
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: { xs: 30, sm: 34 },
                                        height: { xs: 30, sm: 34 },
                                        bgcolor: NAVY,
                                        fontSize: 14,
                                    }}
                                >
                                    {auth.user?.fullName?.charAt(0)?.toUpperCase()}
                                </Avatar>
                                <span className="hidden max-w-[120px] truncate text-sm font-semibold text-slate-800 lg:block">
                                    {auth.user?.fullName}
                                </span>
                            </IconButton>
                        ) : (
                            <Button
                                onClick={() => handleDirectNavigation("/login")}
                                variant="contained"
                                size="small"
                                disableElevation
                                sx={{
                                    bgcolor: NAVY,
                                    color: "#FFFFFF",
                                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                                    px: { xs: 1.5, sm: 2.5 },
                                    py: 0.7,
                                    fontWeight: 700,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    "&:hover": { bgcolor: "#334155" },
                                }}
                            >
                                Login
                            </Button>
                        )}

                        <IconButton
                            onClick={() => handleDirectNavigation("/wishlist")}
                            aria-label={`Wishlist, ${wishlist.wishList?.products?.length || 0} items`}
                            sx={{
                                color: NAVY,
                                p: { xs: 1, sm: 1.25 },
                                "&:hover": { backgroundColor: "#F8FAFC" },
                            }}
                        >
                            <Badge
                                badgeContent={wishlist.wishList?.products?.length || 0}
                                color="error"
                                max={99}
                            >
                                <FavoriteBorder sx={{ fontSize: { xs: 21, sm: 24 } }} />
                            </Badge>
                        </IconButton>

                        <IconButton
                            onClick={() => handleDirectNavigation("/cart")}
                            aria-label={`Shopping cart, ${cart.cart?.cartItems?.length || 0} items`}
                            sx={{
                                color: NAVY,
                                p: { xs: 1, sm: 1.25 },
                                "&:hover": { backgroundColor: "#F8FAFC" },
                            }}
                        >
                            <Badge
                                badgeContent={cart.cart?.cartItems?.length || 0}
                                color="error"
                                max={99}
                            >
                                <AddShoppingCart sx={{ fontSize: { xs: 22, sm: 25 } }} />
                            </Badge>
                        </IconButton>
                    </div>
                </div>

                {/* Mobile search / desktop category navigation */}
                {!isLarge ? (
                    <div className="border-t border-slate-100 bg-white px-3 py-3">
                        <SearchForm
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            handleSearchSubmit={handleSearchSubmit}
                            searchInputRef={searchInputRef}
                        />
                    </div>
                ) : (
                    <div
                        className="relative border-t border-slate-100 bg-white px-4 lg:px-20"
                        onMouseLeave={() => setShowCategorySheet(false)}
                    >
                        <ul className="flex items-center gap-1 overflow-x-auto font-medium text-sm">
                            {mainCategories.map((item) => {
                                const isSelected = selectedCategory === item.categoryId;

                                return (
                                    <li key={item.categoryId} className="shrink-0">
                                        <button
                                            type="button"
                                            onMouseEnter={() => {
                                                setSelectedCategory(item.categoryId);
                                                setShowCategorySheet(true);
                                            }}
                                            onFocus={() => {
                                                setSelectedCategory(item.categoryId);
                                                setShowCategorySheet(true);
                                            }}
                                            onClick={() => {
                                                setSelectedCategory(item.categoryId);
                                                setShowCategorySheet((open) =>
                                                    selectedCategory === item.categoryId
                                                        ? !open
                                                        : true
                                                );
                                            }}
                                            aria-expanded={
                                                showCategorySheet && isSelected
                                            }
                                            className={`flex items-center gap-1 border-b-2 px-4 py-3 transition-colors ${
                                                isSelected && showCategorySheet
                                                    ? "border-orange-500 bg-orange-50/60 text-slate-950"
                                                    : "border-transparent text-slate-700 hover:border-orange-400 hover:bg-slate-50 hover:text-slate-950"
                                            }`}
                                        >
                                            {item.name}
                                            <ExpandMore sx={{ fontSize: 17 }} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>

                        {showCategorySheet && (
                            <div
                                className="categorySheet absolute left-4 right-4 top-full z-50 overflow-hidden rounded-b-xl border border-slate-200 bg-white shadow-xl lg:left-20 lg:right-20"
                                onMouseEnter={() => setShowCategorySheet(true)}
                            >
                                <CategorySheet
                                    selectedCategory={selectedCategory}
                                    categories={categories}
                                    onClose={() => setShowCategorySheet(false)}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Box>

            {/* Responsive mobile navigation drawer */}
            <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={() => setMobileMenuOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{ zIndex: 1300 }}
                PaperProps={{
                    sx: {
                        width: { xs: "88vw", sm: 340 },
                        maxWidth: 360,
                        backgroundColor: "#FFFFFF",
                    },
                }}
            >
                <Box className="flex h-full flex-col bg-white">
                    <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4">
                        <button
                            type="button"
                            onClick={() => handleDirectNavigation("/")}
                            aria-label="Power Tools home"
                        >
                            <KithimuLogo />
                        </button>

                        <IconButton
                            onClick={() => setMobileMenuOpen(false)}
                            aria-label="Close navigation menu"
                            size="small"
                            sx={{ color: NAVY }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div className="border-b border-slate-100 px-4 py-3">
                        <SearchForm
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            handleSearchSubmit={handleSearchSubmit}
                            searchInputRef={searchInputRef}
                        />
                    </div>

                    <div className="flex-1 overflow-y-auto px-3 py-4">
                        <p className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400">
                            Browse the store
                        </p>

                        <List component="nav" disablePadding>
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleDirectNavigation("/")}
                                    sx={{
                                        py: 1.2,
                                        px: 1.5,
                                        borderRadius: 2,
                                        color: NAVY,
                                    }}
                                >
                                    <ListItemText
                                        primary="Home"
                                        primaryTypographyProps={{
                                            fontSize: "0.925rem",
                                            fontWeight: 700,
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>

                            <Divider sx={{ my: 1, borderColor: "#F1F5F9" }} />

                            {mainCategories.map((mainCat) => {
                                const isExpanded =
                                    expandedCategory === mainCat.categoryId;
                                const subCategories = getSubCategories(mainCat);

                                return (
                                    <React.Fragment key={mainCat.categoryId}>
                                        <ListItem disablePadding>
                                            <ListItemButton
                                                onClick={() =>
                                                    handleCategoryToggle(mainCat.categoryId)
                                                }
                                                sx={{
                                                    py: 1.1,
                                                    px: 1.5,
                                                    borderRadius: 2,
                                                    color: isExpanded ? "#C2410C" : NAVY,
                                                    "&:hover": {
                                                        backgroundColor: "#F8FAFC",
                                                    },
                                                }}
                                            >
                                                <ListItemText
                                                    primary={mainCat.name}
                                                    primaryTypographyProps={{
                                                        fontSize: "0.9rem",
                                                        fontWeight: isExpanded ? 700 : 600,
                                                    }}
                                                />

                                                {subCategories.length > 0 &&
                                                    (isExpanded ? (
                                                        <ExpandLess sx={{ fontSize: 20 }} />
                                                    ) : (
                                                        <ExpandMore sx={{ fontSize: 20 }} />
                                                    ))}
                                            </ListItemButton>
                                        </ListItem>

                                        <Collapse
                                            in={isExpanded}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <Box className="my-1 rounded-xl border-l-2 border-orange-500 bg-slate-50 py-2 pl-2 pr-1">
                                                <ListItemButton
                                                    onClick={() =>
                                                        handleDirectNavigation(
                                                            `/products/${mainCat.categoryId}`
                                                        )
                                                    }
                                                    sx={{
                                                        py: 0.7,
                                                        px: 1,
                                                        borderRadius: 1.5,
                                                        color: "#C2410C",
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={`All ${mainCat.name}`}
                                                        primaryTypographyProps={{
                                                            fontSize: "0.825rem",
                                                            fontWeight: 700,
                                                        }}
                                                    />
                                                </ListItemButton>

                                                <List disablePadding>
                                                    {subCategories.map((subCat) => {
                                                        const isSubExpanded =
                                                            expandedSubCategory ===
                                                            subCat.categoryId;
                                                        const itemCategories =
                                                            getItemCategories(subCat);

                                                        return (
                                                            <React.Fragment
                                                                key={subCat.categoryId}
                                                            >
                                                                <ListItemButton
                                                                    onClick={() => {
                                                                        if (
                                                                            itemCategories.length > 0
                                                                        ) {
                                                                            handleSubCategoryToggle(
                                                                                subCat.categoryId
                                                                            );
                                                                        } else {
                                                                            handleDirectNavigation(
                                                                                `/products/${subCat.categoryId}`
                                                                            );
                                                                        }
                                                                    }}
                                                                    sx={{
                                                                        py: 0.65,
                                                                        px: 1,
                                                                        borderRadius: 1.5,
                                                                        color: "#334155",
                                                                    }}
                                                                >
                                                                    <ListItemText
                                                                        primary={subCat.name}
                                                                        primaryTypographyProps={{
                                                                            fontSize: "0.85rem",
                                                                            fontWeight: 500,
                                                                        }}
                                                                    />

                                                                    {itemCategories.length > 0 &&
                                                                        (isSubExpanded ? (
                                                                            <ExpandLess
                                                                                sx={{
                                                                                    fontSize: 18,
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <ExpandMore
                                                                                sx={{
                                                                                    fontSize: 18,
                                                                                }}
                                                                            />
                                                                        ))}
                                                                </ListItemButton>

                                                                {itemCategories.length > 0 && (
                                                                    <Collapse
                                                                        in={isSubExpanded}
                                                                        timeout="auto"
                                                                        unmountOnExit
                                                                    >
                                                                        <List
                                                                            disablePadding
                                                                            sx={{ pl: 2 }}
                                                                        >
                                                                            {itemCategories.map(
                                                                                (itemCat) => (
                                                                                    <ListItemButton
                                                                                        key={
                                                                                            itemCat.categoryId
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleDirectNavigation(
                                                                                                `/products/${itemCat.categoryId}`
                                                                                            )
                                                                                        }
                                                                                        sx={{
                                                                                            py: 0.55,
                                                                                            px: 1,
                                                                                            borderRadius: 1.5,
                                                                                            color: "#64748B",
                                                                                            "&:hover":
                                                                                                {
                                                                                                    color: "#C2410C",
                                                                                                    backgroundColor:
                                                                                                        "#FFFFFF",
                                                                                                },
                                                                                        }}
                                                                                    >
                                                                                        <ListItemText
                                                                                            primary={
                                                                                                itemCat.name
                                                                                            }
                                                                                            primaryTypographyProps={{
                                                                                                fontSize:
                                                                                                    "0.8rem",
                                                                                            }}
                                                                                        />
                                                                                    </ListItemButton>
                                                                                )
                                                                            )}
                                                                        </List>
                                                                    </Collapse>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </List>
                                            </Box>
                                        </Collapse>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </div>

                    <div className="border-t border-slate-100 bg-slate-50 px-5 py-4">
                        <a
                            href="tel:0720380744"
                            className="flex items-center gap-2 text-sm font-semibold text-slate-700"
                        >
                            <PhoneIcon sx={{ fontSize: 18, color: AMBER }} />
                            Customer Support: 0720 380 744
                        </a>
                    </div>
                </Box>
            </Drawer>
        </>
    );
};

export default Navbar;